import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { CellType, IChangeCheckboxEventArgs, IColumnMovingEventArgs, IGridState, IgxDialogComponent, IgxGridComponent, IgxGridStateDirective } from '@infragistics/igniteui-angular';
import { Subject, takeUntil } from 'rxjs';
import { UserService } from 'src/app/authentication/user.service';
import { PackageState } from 'src/app/models/enums/packageState';
import { GridState } from 'src/app/models/grid-state';
import { PackageRequest } from 'src/app/models/package-request';
import { Program } from 'src/app/models/program';
import { ProgramsService } from 'src/app/services/programs.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { restoreState } from 'src/app/shared/utils';
import { InfoEnrollmentComponent } from '../info-enrollment/info-enrollment.component';
import { ProcessEnrollmentComponent } from '../process-enrollment/process-enrollment.component';

@Component({
    selector: 'app-edit-enrollments',
    templateUrl: './edit-enrollments.component.html',
    styleUrls: ['./edit-enrollments.component.scss']
})
export class EditEnrollmentsComponent implements OnInit, OnDestroy {
    @ViewChild('enrollment', { static: true, read: ProcessEnrollmentComponent })
    enrollment!: ProcessEnrollmentComponent;

    @ViewChild(IgxGridStateDirective, { static: true })
    private state!: IgxGridStateDirective;

    @ViewChild('enroll', { static: true, read: IgxGridComponent })
    private grid!: IgxGridComponent;

    @ViewChild('enrollmentDialog', { static: true, read: IgxDialogComponent })
    private enrollmentDialog!: IgxDialogComponent;

    public requests: PackageRequest[] = [];
    public data: PackageRequest[] = [];
    public showAll = false;
    private programs: Program[] = [];
    private destroy$: Subject<void> = new Subject<void>();
    private gridName: string = 'edit-enrollments-grid';
    private gridState?: GridState;

    public constructor(
        private programService: ProgramsService,
        private waitIndicatorService: WaitIndicatorService,
        private userService: UserService,
        router: Router
    ) {
        router.events.pipe(takeUntil(this.destroy$)).subscribe(e => {
            if (e instanceof NavigationStart) {
                if (this.gridState) {
                    this.gridState.options = this.state.getState().toString();
                    this.gridState.userId = this.userService.currentUser!.id!;
                } else {
                    this.gridState = {
                        gridName: this.gridName,
                        options: this.state.getState().toString(),
                        userId: this.userService.currentUser!.id!,
                    };
                }
                this.userService.updateGridState(this.gridState);
            }
        });
        this.load();
    }

    ngOnInit(): void {
        this.userService.gridStates().pipe(takeUntil(this.destroy$)).subscribe((states: GridState[]) => {
            const loaded = states.filter(s => s.gridName === this.gridName && s.userId.replaceAll('-','') === this.userService.currentUser!.id!)[0];
            if (loaded) {
                this.gridState = loaded;
                const gridState: IGridState = JSON.parse(this.gridState?.options ?? '');
                restoreState(gridState, this.grid);
            }
        });
        this.enrollment.done.pipe(takeUntil(this.destroy$)).subscribe(e => {
            if (e === PackageState.Deleted) {
                this.requests = this.requests.filter(r => r.id !== this.enrollment.request?.id);
                this.data = this.data.filter(r => r.id !== this.enrollment.request?.id);
            }
            this.enrollmentDialog.close();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    protected filterRequests = (e: IChangeCheckboxEventArgs): void => {
        this.showAll = e.checked;
        if (e.checked) {
            this.data = this.requestToData(this.requests);
        } else {
            this.data = this.requestToData(this.requests.filter(r => r.state === PackageState.New));
        }
    }

    protected renderProgram = (cell: CellType): string => {
        return this.programs.find(p => p.id === cell.value.replaceAll('-', ''))?.name ?? '';
    }

    protected startProcessEnrollment = (dialog: IgxDialogComponent, packageRequest: PackageRequest): void => {
        this.waitIndicatorService.show();
        this.enrollment.kid = undefined;
        this.enrollment.parent = undefined;
        this.enrollment.secondParent = undefined;
        this.enrollment.request = packageRequest;
        this.enrollment.start().then(e => {
            this.waitIndicatorService.hide();
            dialog.open();
        });
    }

    protected restore = (packageRequest: PackageRequest): void => {
        packageRequest.state = PackageState.New;
        this.programService.packageRequestToNew(packageRequest.id!);
    }

    protected showInfo = (data: PackageRequest, info: InfoEnrollmentComponent, dialog: IgxDialogComponent): void => {
        info.load(data.id?.replaceAll('-', '')!)
        dialog.open();
    }

    protected columnMoving = (e: IColumnMovingEventArgs): void => {
        e.cancel = e.source.pinned;
    }

    private load = async (): Promise<void> => {
        this.waitIndicatorService.show();
        const data = await this.programService.packageRequestAll()
        this.requests = data.packages;
        this.programs = data.programs;
        this.filterRequests({ checked: false });
        this.waitIndicatorService.hide();
    }

    private requestToData = (request: PackageRequest[]): PackageRequest[] => {
        request.forEach(r => {
            r.date = r.dates[0];
        });
        return request;
    }
}
